import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  FiltrosPainel,
  PastaTranscrita,
  ResumoTranscricoes,
  PERIODOS_PAINEL,
  formatarNumero,
  formatarPercentual,
  formatarDataHoraCurta,
} from '../services/transcricao';
import { colors } from '../theme/expertiseTheme';

/**
 * Painel de indicadores e histórico das pastas transcritas
 */
type Props = {
  filtros: FiltrosPainel;
  onChange: (filtros: FiltrosPainel) => void;
  projetos: string[];
  resumo: ResumoTranscricoes | null;
  historico: PastaTranscrita[];
  loading: boolean;
};

const TranscricaoPainel: React.FC<Props> = ({
  filtros,
  onChange,
  projetos,
  resumo,
  historico,
  loading,
}) => {
  const indicadores = [
    {
      titulo: 'Pastas Transcritas',
      valor: formatarNumero(resumo?.pastasTranscritas),
      cor: colors.dark,
    },
    {
      titulo: 'Áudios Recebidos',
      valor: formatarNumero(resumo?.audiosRecebidos),
      cor: colors.dark,
    },
    {
      titulo: 'Transcrições',
      valor: formatarNumero(resumo?.transcricoes),
      cor: colors.dark,
    },
    {
      titulo: 'Não Transcritos',
      valor: formatarNumero(resumo?.naoTranscritos),
      cor: colors.dark,
      legenda: 'Perda de áudios',
    },
    {
      titulo: 'Taxa de Perda',
      valor: formatarPercentual(resumo?.taxaPerda),
      cor: (resumo?.taxaPerda || 0) > 0 ? colors.error : colors.primary,
    },
    {
      titulo: 'Última Transcrição',
      valor: formatarDataHoraCurta(resumo?.ultimaTranscricao),
      cor: colors.dark,
    },
  ];

  return (
    <Box>
      {/* Filtros */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: colors.text.secondary,
              mb: 1,
            }}
          >
            Período
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={filtros.periodoDias}
            onChange={(event) =>
              onChange({ ...filtros, periodoDias: Number(event.target.value) })
            }
          >
            {PERIODOS_PAINEL.map((periodo) => (
              <MenuItem key={periodo.value} value={periodo.value}>
                {periodo.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: colors.text.secondary,
              mb: 1,
            }}
          >
            Projeto
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={filtros.projeto}
            onChange={(event) => onChange({ ...filtros, projeto: event.target.value })}
          >
            <MenuItem value="">Todos os projetos</MenuItem>
            {projetos.map((projeto) => (
              <MenuItem key={projeto} value={projeto}>
                {projeto}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: colors.text.secondary,
              mb: 1,
            }}
          >
            Buscar Pasta
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="ID da pasta"
            value={filtros.pastaId}
            onChange={(event) => onChange({ ...filtros, pastaId: event.target.value })}
          />
        </Grid>
      </Grid>

      {/* Indicadores */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {indicadores.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.titulo}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: 3,
                border: `1px solid ${colors.border}`,
                borderRadius: '16px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: colors.text.secondary,
                  mb: 1,
                }}
              >
                {item.titulo}
              </Typography>

              {loading ? (
                <Skeleton variant="text" width={120} height={44} />
              ) : (
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: item.cor, fontSize: '1.75rem' }}
                >
                  {item.valor}
                </Typography>
              )}

              {item.legenda && (
                <Typography sx={{ fontSize: '0.75rem', color: colors.text.light, mt: 0.5 }}>
                  {item.legenda}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Histórico de pastas */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark }}>
            Histórico de pastas transcritas
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Compare o total recebido com o que realmente foi transcrito em cada pasta.
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data e hora</TableCell>
                <TableCell>Pasta transcrita</TableCell>
                <TableCell>Projeto</TableCell>
                <TableCell align="right">Recebidos</TableCell>
                <TableCell align="right">Transcritos</TableCell>
                <TableCell align="right">Não transcritos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && historico.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Skeleton variant="text" height={32} />
                    <Skeleton variant="text" height={32} />
                    <Skeleton variant="text" height={32} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && historico.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text.secondary, textAlign: 'center', py: 2 }}
                    >
                      Nenhuma pasta transcrita no período selecionado.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {historico.map((pasta) => (
                <TableRow key={pasta.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      {new Date(pasta.dataHora).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Tooltip title={pasta.pastaId}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: colors.dark,
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {pasta.pastaId}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
                      {pasta.projeto || '-'}
                    </Typography>
                    {pasta.subprojeto && (
                      <Typography sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                        {pasta.subprojeto}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
                      {formatarNumero(pasta.recebidos)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary }}>
                      {formatarNumero(pasta.transcritos)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: pasta.naoTranscritos > 0 ? colors.error : colors.text.secondary,
                      }}
                    >
                      {formatarNumero(pasta.naoTranscritos)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TranscricaoPainel;
